import React, { useState, useEffect } from 'react';
import { Table, Button, Form, InputGroup, Row, Col, Card, Dropdown, OverlayTrigger, Tooltip, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ConfirmationModal from '../common/ConfirmationModal';
import { FaEdit, FaTrash, FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp, FaEllipsisV, FaCalendarAlt, FaTag, FaInfoCircle } from 'react-icons/fa';
import { clientApi } from '../../services/api';
import { CLIENT_STATUSES } from '../../utils/clientModel';
import { toast } from 'react-toastify';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortField, setSortField] = useState('lastName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterField, setDateFilterField] = useState('nextFollowUp'); // Default to Next Follow Up
  const [showFilters, setShowFilters] = useState(false); // For collapsible filter section
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(10);
  const [paginatedClients, setPaginatedClients] = useState([]);

  // Load clients from API
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const loadedClients = await clientApi.getAll();
        setClients(loadedClients);
        setFilteredClients(loadedClients);
      } catch (error) {
        toast.error('Failed to load clients');
        console.error('Error loading clients:', error);
      }
    };
    
    fetchClients();
  }, []);

  // Filter and sort clients when filter criteria change
  useEffect(() => {
    let result = [...clients];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        client => 
          client.firstName.toLowerCase().includes(term) ||
          client.lastName.toLowerCase().includes(term) ||
          (client.company && client.company.toLowerCase().includes(term)) ||
          (client.email && client.email.toLowerCase().includes(term)) ||
          (client.phone && client.phone.toLowerCase().includes(term)) ||
          (client.alternatePhone && client.alternatePhone.toLowerCase().includes(term)) ||
          (client.remarks && client.remarks.toLowerCase().includes(term)) ||
          (client.tags && client.tags.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      result = result.filter(client => client.status === statusFilter);
    }
    
    // Apply tag filter
    if (tagFilter) {
      result = result.filter(client => {
        if (!client.tags) return false;
        return client.tags.toLowerCase().includes(tagFilter.toLowerCase());
      });
    }
    
    // Apply date range filter
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Set to end of day
      
      result = result.filter(client => {
        if (!client[dateFilterField]) return false;
        const date = new Date(client[dateFilterField]);
        return date >= start && date <= end;
      });
    } else if (startDate) {
      const start = new Date(startDate);
      result = result.filter(client => {
        if (!client[dateFilterField]) return false;
        return new Date(client[dateFilterField]) >= start;
      });
    } else if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Set to end of day
      result = result.filter(client => {
        if (!client[dateFilterField]) return false;
        return new Date(client[dateFilterField]) <= end;
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let fieldA = a[sortField] ? a[sortField].toString().toLowerCase() : '';
      let fieldB = b[sortField] ? b[sortField].toString().toLowerCase() : '';
      
      if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
      if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredClients(result);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [clients, searchTerm, statusFilter, tagFilter, sortField, sortDirection, startDate, endDate, dateFilterField]);
  
  // Handle pagination
  useEffect(() => {
    // Get current clients for page
    const indexOfLastClient = currentPage * clientsPerPage;
    const indexOfFirstClient = indexOfLastClient - clientsPerPage;
    const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
    
    setPaginatedClients(currentClients);
  }, [filteredClients, currentPage, clientsPerPage]);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Format date for display in DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
  
    return `${day}/${month}/${year}`;
  };

  // Calculate total pages
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  // Handle client deletion confirmation
  const handleDeleteConfirmation = (id) => {
    setClientToDelete(id);
    setShowConfirmModal(true);
  };
  
  // Handle actual client deletion
  const handleDelete = async () => {
    if (!clientToDelete) return;
    
    try {
      await clientApi.delete(clientToDelete);
      // Update local state
      setClients(clients.filter(client => client.id !== clientToDelete));
      setFilteredClients(filteredClients.filter(client => client.id !== clientToDelete));
      toast.success('Client deleted successfully');
    } catch (error) {
      toast.error('Failed to delete client');
      console.error('Error deleting client:', error);
    } finally {
      setShowConfirmModal(false);
      setClientToDelete(null);
    }
  };
  
  // Handle cancel delete
  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setClientToDelete(null);
  };

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? <FaSortAmountUp size={14} /> : <FaSortAmountDown size={14} />;
    }
    return null;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case CLIENT_STATUSES.LEAD:
        return 'status-badge-lead';
      case CLIENT_STATUSES.CONNECTED_PENDING:
        return 'status-badge-connected-pending';
      case CLIENT_STATUSES.CONNECTED_FOLLOWUP:
        return 'status-badge-connected-followup';
      case CLIENT_STATUSES.REJECTED:
        return 'status-badge-rejected';
      case CLIENT_STATUSES.NOT_CONNECTED:
        return 'status-badge-not-connected';
      case CLIENT_STATUSES.CALL_BACK:
        return 'status-badge-call-back';
      case CLIENT_STATUSES.CLOSED:
        return 'status-badge-closed';
      default:
        return 'status-badge-default';
    }
  };
  
  // Render tags as badges
  const renderTags = (tags) => {
    if (!tags) return null;
    
    return tags.split(',').map(tag => {
      // Assign different colors based on tag content
      let badgeClass = 'tag-badge-default';
      const tagLower = tag.trim().toLowerCase();
      
      if (tagLower.includes('imp')) badgeClass = 'tag-badge-imp';
      if (tagLower.includes('v imp')) badgeClass = 'tag-badge-vimp';
      if (tagLower.includes('amazon')) badgeClass = 'tag-badge-amazon';
      if (tagLower.includes('flipkart')) badgeClass = 'tag-badge-flipkart';
      if (tagLower.includes('meesho')) badgeClass = 'tag-badge-meesho';
      if (tagLower.includes('myntra')) badgeClass = 'tag-badge-myntra';
      
      return (
        <span key={tag} className={`tag-badge ${badgeClass} me-1 mb-1`}>
          <FaTag className="tag-icon me-1" size={10} />
          {tag.trim()}
        </span>
      );
    });
  };

  return (
    <div>
      <Card className="mb-4 client-list-card">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col md={6}>
              <h2 className="mb-0 section-title">
                <span className="highlight-text">Client</span> Management
              </h2>
              <p className="text-muted mt-2">
                <FaInfoCircle className="me-1" /> 
                Showing {filteredClients.length} of {clients.length} clients
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <Link to="/add" className="btn btn-primary btn-glow">
                <span className="btn-text-wrapper">Add New Client</span>
              </Link>
            </Col>
          </Row>
          
          <Card className="filter-card mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center" 
              onClick={() => setShowFilters(!showFilters)} 
              style={{ cursor: 'pointer' }}
            >
              <h5 className="mb-0">Filter Clients</h5>
              <Button variant="link" className="p-0">
                {showFilters ? (
                  <FaFilter className="me-1" /> 
                ) : (
                  <FaFilter className="me-1" />
                )}
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Card.Header>
            
            {/* Basic search always visible */}
            <Card.Body className="py-2">
              <Row className="mb-2">
                <Col md={6}>
                  <InputGroup className="search-input">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-control"
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup className="filter-input">
                    <InputGroup.Text>
                      <FaFilter />
                    </InputGroup.Text>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="status-select"
                    >
                      <option value="">All Statuses</option>
                      {Object.values(CLIENT_STATUSES).map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <Button 
                    variant="outline-secondary" 
                    className="w-100 reset-all-btn"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('');
                      setTagFilter('');
                      setStartDate('');
                      setEndDate('');
                      setDateFilterField('nextFollowUp');
                    }}
                  >
                    Reset All
                  </Button>
                </Col>
              </Row>
              
              {/* Advanced filters - collapsible */}
              {showFilters && (
                <>
                  <Row className="mb-2 mt-2">
                    <Col md={11}>
                      <InputGroup className="filter-input">
                        <InputGroup.Text>
                          <FaTag />
                        </InputGroup.Text>
                        <Form.Control
                          placeholder="Filter by tag..."
                          value={tagFilter}
                          onChange={(e) => setTagFilter(e.target.value)}
                          className="tag-input"
                        />
                      </InputGroup>
                    </Col>
                    <Col md={1} className="d-flex align-items-center">
                      <Button 
                        variant="outline-secondary" 
                        className="w-100 clear-btn"
                        onClick={() => setTagFilter('')}
                        disabled={!tagFilter}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
              
                  <Row className="date-filter-row">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="filter-label">
                          <FaCalendarAlt className="me-1" /> Date Field
                        </Form.Label>
                        <Form.Select
                          value={dateFilterField}
                          onChange={(e) => setDateFilterField(e.target.value)}
                          className="date-field-select"
                          size="sm"
                        >
                          <option value="nextFollowUp">Next Follow-up Date</option>
                          <option value="createdAt">Created Date</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="filter-label">From Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="date-input"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="filter-label">To Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="date-input"
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={1} className="d-flex align-items-end">
                      <Button 
                        variant="outline-secondary" 
                        className="w-100 clear-btn"
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                        }}
                        size="sm"
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>
          
          {filteredClients.length === 0 ? (
            <div className="empty-state text-center py-5">
              <div className="empty-icon mb-3">🔍</div>
              <h4>No clients found</h4>
              <p className="text-muted">Try adjusting your filters or add a new client to get started.</p>
              <Link to="/add" className="btn btn-primary btn-sm mt-2">
                Add New Client
              </Link>
            </div>
          ) : (
            <div className="table-responsive data-table-container">
              <Table hover className="align-middle client-table">
                <thead>
                  <tr className="table-header-row">
                    <th onClick={() => handleSort('lastName')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Name</span>
                        <span className="ms-1">{renderSortIcon('lastName')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('phone')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Mobile</span>
                        <span className="ms-1">{renderSortIcon('phone')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('alternatePhone')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Alt. Mobile</span>
                        <span className="ms-1">{renderSortIcon('alternatePhone')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('remarks')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Remarks</span>
                        <span className="ms-1">{renderSortIcon('remarks')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('firstContactDate')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>First Contact</span>
                        <span className="ms-1">{renderSortIcon('firstContactDate')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('nextFollowUp')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Next Follow-up</span>
                        <span className="ms-1">{renderSortIcon('nextFollowUp')}</span>
                      </div>
                    </th>
                    <th onClick={() => handleSort('status')} className="sortable-header">
                      <div className="d-flex align-items-center">
                        <span>Status</span>
                        <span className="ms-1">{renderSortIcon('status')}</span>
                      </div>
                    </th>
                    <th className="sortable-header">Tags</th>
                    <th className="sortable-header">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client) => (
                    <tr key={client.id} className="client-row">
                      <td>
                        <Link to={`/client/${client.id}`} className="client-name-link">
                          {client.firstName} {client.lastName}
                        </Link>
                      </td>
                      <td className="phone-cell">{client.phone}</td>
                      <td className="alt-phone-cell">{client.alternatePhone || '-'}</td>
                      <td className="remarks-cell">
                        {client.remarks ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{client.remarks}</Tooltip>}
                          >
                            <div className="remarks-text">
                              {client.remarks.length > 30 ? 
                                `${client.remarks.substring(0, 30)}...` : 
                                client.remarks}
                            </div>
                          </OverlayTrigger>
                        ) : '-'}
                      </td>
                      <td className="date-cell">
                        {client.firstContactDate ? (
                          <div className="date-wrapper">
                            <div className="date-primary">
                              {formatDate(client.firstContactDate)}
                            </div>
                            <div className="date-secondary">
                              {new Date(client.firstContactDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td className="date-cell">
                        {client.nextFollowUp ? (
                          <div className="date-wrapper">
                            <div className="date-primary">
                              {formatDate(client.nextFollowUp)}
                            </div>
                            <div className="date-secondary">
                              {new Date(client.nextFollowUp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(client.status)}`}>
                          {client.status}
                        </span>
                      </td>
                      <td className="tags-cell">
                        <div className="tags-container">
                          {renderTags(client.tags)}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex">
                          <Dropdown>
                            <Dropdown.Toggle variant="light" size="sm" className="action-dropdown">
                              <FaEllipsisV />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="action-menu">
                              <Dropdown.Item as={Link} to={`/client/${client.id}`} className="action-item">
                                <FaInfoCircle className="me-2" /> View Details
                              </Dropdown.Item>
                              <Dropdown.Item as={Link} to={`/edit/${client.id}`} className="action-item">
                                <FaEdit className="me-2" /> Edit
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                onClick={() => handleDeleteConfirmation(client.id)} 
                                className="action-item text-danger"
                              >
                                <FaTrash className="me-2" /> Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container mt-4">
                  <Pagination className="justify-content-center">
                    <Pagination.First
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    
                    {/* Show page numbers */}
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show current page, and 1 page before and after
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Item
                            key={pageNumber}
                            active={pageNumber === currentPage}
                            onClick={() => paginate(pageNumber)}
                            className="pagination-item"
                          >
                            {pageNumber}
                          </Pagination.Item>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return <Pagination.Ellipsis key={`ellipsis-${pageNumber}`} />;
                      }
                      return null;
                    })}
                    
                    <Pagination.Next
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                  
                  <div className="text-center mt-2 pagination-info">
                    Showing {paginatedClients.length} of {filteredClients.length} clients
                    (Page {currentPage} of {totalPages})
                  </div>
                </div>
              )}
            </div>
          )}
          

        </Card.Body>
      </Card>
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showConfirmModal}
        title="Confirm Delete"
        message="Are you sure you want to delete this client? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ClientList;
